/**
 * #685 — the operational Clients' Map, extracted from WebMapRoute so it can
 * render OUTSIDE the command-center shell (the caregiver homepage embeds it
 * directly; the goal is the map on the page, not a link to a page).
 *
 * Owns everything except the chrome: GMA-1741 live client home pins from
 * GET /api/v1/clients (geocoded lat/lng, GMA-1697) via clientPinMapper;
 * GMA-1693 IPAWS impact zones; #555 zone-coded coverage note; GMA-1742 route
 * corridors + telemetry sink. The demo roster still merges in under
 * REACT_APP_DEMO_DATA=1, and any fetch failure degrades to the demo/empty
 * state — never a white screen.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { useFetch } from "../../api/baseApi";
import {
  MapPage,
  mapLiveClientPins,
  mapImpactZones,
  mapZoneCodedWatches,
  ZoneCodedCoverageNote,
  mapRouteCorridors,
  rosterClientIdsOf,
  setTelemetry,
} from "@graymattersalliance/compass-map";
import { captureMessage, addBreadcrumb } from "../../config/sentry";
import {
  fetchClientLatestHealthMetrics,
  fetchWatchIntegrationEnabled,
} from "../../api/watchService";
import firebaseApp from "../../firebaseConfig";
import { VITALS_FRESH_WINDOW_MS, VITALS_REQUEST_TIMEOUT_MS } from "../clientProfile/WatchVitalsSection";
import {
  DEMO_DATA_ENABLED,
  getDemoClients,
  getDemoCorridors,
} from "./demoData";
import { elopementClientsFromPins } from "../../utils/elopementClientsFromPins";
import {
  vitalsFromLatestMetrics,
  attachVitalsToPins,
  hasOnlineWatchDevice,
  watchEnabledClientIds,
} from "./mapWatchVitals";
import { attachTriggersToOverlays } from "./mapWatchTriggers";
import PropTypes from "prop-types";
import { isElopementBoundaryEnabled } from "../../utils/featureFlags";
import ElopementBoundaryControls from "../../components/web/ElopementBoundaryControls";

// The package reads no environment (it serves a Vite app and this CRA app);
// this module owns mapping the CRA env convention onto MapPage props, and
// injects the app's Sentry sink for the corridor span/ETA telemetry (GMA-1742).
setTelemetry(captureMessage);
const MAPS_API_KEY = process.env.REACT_APP_AWS_MAPS_API_KEY || "";
const MAPS_REGION = process.env.REACT_APP_AWS_MAPS_REGION || "us-east-2";
/** Re-resolve vitals so a reading that crosses the live boundary drops off
 *  while the map stays open (Copilot on #706). Cap well below the 1h window. */
const VITALS_REFRESH_MS = Math.min(60 * 1000, Math.floor(VITALS_FRESH_WINDOW_MS / 4));

/**
 * Settle a hung fetch the same way WatchVitalsSection does — `apiRequest`'s
 * fetch is unbounded, so without this one slow client keeps the whole
 * allSettled batch pending and the next minute tick stacks another (Copilot
 * on #706).
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        Object.assign(new Error(`Watch vitals request timed out after ${ms}ms`), {
          name: "VitalsTimeout",
        }),
      );
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * One-shot Firebase devices map for a client. Missing snapshot → null
 * (enrolled-but-offline / no devices). Transport/DB errors reject so the
 * caller can count `requestFailed` instead of `watchOffline` (Copilot #710).
 * @param {string|number} userId
 * @returns {Promise<object|null>}
 */
async function fetchDevicesMapOnce(userId) {
  const db = getDatabase(firebaseApp);
  const snap = await get(ref(db, `status/${userId}/devices`));
  return snap.exists() ? snap.val() : null;
}

/**
 * #703 — latest vitals for Watch-enrolled clients that are online AND have a
 * live reading. Roster `appleWatchEnabled === true` is the candidate filter
 * (no per-client gate fan-out for the rest of the roster). The authoritative
 * toggle is still GET watch-integration (ClientProfilePage / GMA-1865),
 * confirmed for those candidates before health/presence so a stale true
 * cannot expose vitals after disable (Copilot on #706). Failures swallow to
 * "no vitals" so the map never loses pins because health/presence is down.
 */
function useWatchVitals(enabledIds) {
  const [vitalsByClientId, setVitalsByClientId] = useState({});
  const [tick, setTick] = useState(0);
  // Stable dependency: the ids themselves, not the array identity, so a
  // re-render with an equal roster does not re-open the fetches.
  const idKey = enabledIds.join(",");
  const prevIdKeyRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const handle = setInterval(() => setTick((t) => t + 1), VITALS_REFRESH_MS);
    return () => clearInterval(handle);
  }, []);

  useEffect(() => {
    const ids = idKey === "" ? [] : idKey.split(",");
    // Clear only when the enrolled set changes (or empties). A periodic tick
    // must not blank the pins while the next fan-out settles — that flickered
    // the vitals row every VITALS_REFRESH_MS (review finding on #706).
    const rosterChanged = prevIdKeyRef.current !== idKey;
    if (rosterChanged) {
      prevIdKeyRef.current = idKey;
      setVitalsByClientId({});
      inFlightRef.current = false;
    }
    if (ids.length === 0) {
      return undefined;
    }
    // A hung prior batch (pre-timeout) must not stack another full fan-out on
    // every tick — skip until the in-flight generation settles.
    if (!rosterChanged && inFlightRef.current) {
      return undefined;
    }
    let live = true;
    inFlightRef.current = true;
    Promise.allSettled(
      ids.map(async (id) => {
        try {
          // Authoritative enrollment before any readings (fail closed).
          const enrolled = await withTimeout(
            fetchWatchIntegrationEnabled(id),
            VITALS_REQUEST_TIMEOUT_MS,
          );
          if (enrolled !== true) {
            return {
              id,
              devicesMap: null,
              latest: null,
              skipped: "not-enrolled",
            };
          }
          // Settle presence and health independently — a health outage must
          // not discard a successful devicesMap and look like watchOffline
          // in the operational breadcrumb (Copilot on #706).
          const [devicesSettled, latestSettled] = await Promise.allSettled([
            withTimeout(fetchDevicesMapOnce(id), VITALS_REQUEST_TIMEOUT_MS),
            withTimeout(
              fetchClientLatestHealthMetrics(id, { days: 1 }),
              VITALS_REQUEST_TIMEOUT_MS,
            ),
          ]);
          const devicesFailed = devicesSettled.status !== "fulfilled";
          const healthFailed = latestSettled.status !== "fulfilled";
          return {
            id,
            devicesMap: devicesFailed ? null : devicesSettled.value,
            latest: healthFailed ? null : latestSettled.value,
            devicesFailed,
            healthFailed,
          };
        } catch (_err) {
          // Enrollment timeout/transport — fail closed for this client only.
          return {
            id,
            devicesMap: null,
            latest: null,
            devicesFailed: true,
            healthFailed: true,
          };
        }
      }),
    ).then((results) => {
      if (!live) return;
      inFlightRef.current = false;
      const next = {};
      let withReadings = 0;
      let watchOffline = 0;
      let notEnrolled = 0;
      let requestFailed = 0;
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const {
          id,
          devicesMap,
          latest,
          skipped,
          devicesFailed,
          healthFailed,
        } = result.value;
        if (skipped === "not-enrolled") {
          notEnrolled += 1;
          return;
        }
        if (devicesFailed) {
          // Presence unknown — do not count as offline (API outage ≠ watch off).
          requestFailed += 1;
          return;
        }
        if (!hasOnlineWatchDevice(devicesMap)) {
          watchOffline += 1;
          return;
        }
        if (healthFailed) {
          requestFailed += 1;
          return;
        }
        const vitals = vitalsFromLatestMetrics(latest);
        if (vitals) {
          next[id] = vitals;
          withReadings += 1;
        }
      });
      setVitalsByClientId(next);
      // PHI: counts only — never a name, a coordinate or a reading.
      addBreadcrumb("map.vitals.gate.resolved", {
        category: "map.vitals",
        level: "info",
        enrolledCandidates: ids.length,
        authoritativeNotEnrolled: notEnrolled,
        withLiveReadings: withReadings,
        watchOffline,
        requestFailed,
      });
    });
    return () => {
      live = false;
      // Allow the next effect/tick to schedule — the abandoned batch's
      // `.then` will no-op on `live` and must not leave the guard latched.
      inFlightRef.current = false;
    };
  }, [idKey, tick]);
  return vitalsByClientId;
}

export default function ClientsMapPanel({ enableElopementBoundary = false } = {}) {
  // Capture one timestamp so demo-client generation stays deterministic for
  // the render (matches the other /web route containers).
  const now = Date.now();
  // GMA-1995: draw/save only when the commandcenter build flag is on AND the
  // host explicitly opts in (WebMapRoute). Caregiver homepage never passes this.
  const elopementOn =
    enableElopementBoundary && isElopementBoundaryEnabled();
  const [elopementRing, setElopementRing] = useState([]);
  const [elopementDrawMode, setElopementDrawMode] = useState(false);
  const [elopementStatus, setElopementStatus] = useState("");
  const onElopementClick = useCallback(
    (lngLat) => {
      if (!elopementDrawMode || !lngLat) return;
      setElopementRing((prev) => {
        const open = Array.isArray(prev) ? [...prev] : [];
        // Drop closing duplicate while drawing so we can append.
        if (
          open.length >= 2 &&
          open[0][0] === open[open.length - 1][0] &&
          open[0][1] === open[open.length - 1][1]
        ) {
          open.pop();
        }
        open.push([lngLat.lng, lngLat.lat]);
        return open;
      });
    },
    [elopementDrawMode],
  );
  // GMA-1741: live client home pins from geocoded coordinates. Same
  // session-gated fetch + fail-to-empty posture as the INTEL live feeds; a
  // 401/network error leaves live.clients [] and the page keeps working.
  const { data } = useFetch("clients", { version: "v1" });
  const live = useMemo(() => mapLiveClientPins(data), [data]);
  // #403: the caregiver's roster ids (including un-geocoded clients) — the
  // corridor overlay is scoped to these so an off-roster watch leaked by
  // the API never draws on this caregiver's map.
  const rosterClientIds = useMemo(() => rosterClientIdsOf(data), [data]);
  // GMA-1693: alert impact zones from the live IPAWS watches (same
  // fail-to-empty posture — no zones on error, page keeps working).
  // #403: version pinned explicitly — the deployed contract is /api/v2.
  const { data: watchesData } = useFetch("ipaws/watches", { version: "v2" });
  // #723: the card is the only place a caregiver reads an emergency until
  // INTEL ships, so attach the WATCH_TYPES "why this matters" terms to each
  // zone's card (one table — intelModel — never a copy).
  const zones = useMemo(
    () => attachTriggersToOverlays(mapImpactZones(watchesData)),
    [watchesData],
  );
  // #555: the same response's zone-coded (SAME/UGC) alerts, which carry no
  // geometry and so cannot be drawn. Surfaced above the map as a coverage
  // note — an alert the map cannot show must still be visible somewhere.
  const zoneCoded = useMemo(
    () => mapZoneCodedWatches(watchesData),
    [watchesData],
  );
  // GMA-1742: traffic corridors from the live degraded-access watches (same
  // fail-to-empty posture); the demo corridor merges in under the flag so
  // the overlay is visible without waiting for a live event.
  const { data: routesData } = useFetch("routes/access-watches", {
    version: "v2",
  });
  const corridors = useMemo(() => {
    const live = mapRouteCorridors(routesData, { rosterClientIds });
    return DEMO_DATA_ENABLED ? [...live, ...getDemoCorridors()] : live;
  }, [routesData, rosterClientIds]);
  // #703: vitals reach a pin only when Watch is enrolled, online, AND has a
  // live reading. Strict roster `appleWatchEnabled === true` + geocoded pins
  // are the only candidates (no watch-integration fan-out for the rest —
  // Copilot on #706). Confirmed enrollment still uses GET watch-integration
  // for those ids (GMA-1865). compass-map#8 hides empty rows.
  const pinClientIds = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const rosterEnabled = new Set(
      watchEnabledClientIds(data).map((id) => String(id)),
    );
    return data
      .filter(
        (row) =>
          row &&
          rosterEnabled.has(String(row.id)) &&
          row.latitude !== undefined &&
          row.latitude !== null &&
          row.latitude !== "" &&
          row.longitude !== undefined &&
          row.longitude !== null &&
          row.longitude !== "",
      )
      .map((row) => String(row.id));
  }, [data]);
  const vitalsByClientId = useWatchVitals(pinClientIds);
  const livePins = useMemo(
    () => attachVitalsToPins(live.clients, vitalsByClientId),
    [live.clients, vitalsByClientId],
  );
  const clients = DEMO_DATA_ENABLED
    ? [...livePins, ...getDemoClients(now)]
    : livePins;
  // Dual pins (home + device) share one roster id; elopement APIs are
  // per-client, so the selector must not list the same person twice.
  const elopementClients = useMemo(
    () => elopementClientsFromPins(clients),
    [clients],
  );
  return (
    <>
      <ZoneCodedCoverageNote watches={zoneCoded} />
      {elopementOn ? (
        <div style={{ marginBottom: 10 }}>
          <ElopementBoundaryControls
            clients={elopementClients}
            ring={elopementRing}
            drawMode={elopementDrawMode}
            onRingChange={setElopementRing}
            onDrawModeChange={setElopementDrawMode}
            onStatus={setElopementStatus}
          />
          <div
            data-testid="elopement-boundary-status"
            role="status"
            aria-live="polite"
            style={{
              marginTop: 6,
              color: "#9AA8BC",
              fontSize: 12.5,
              minHeight: "1.2em",
            }}
          >
            {elopementStatus}
          </div>
        </div>
      ) : null}
      <MapPage
        clients={clients}
        zones={zones}
        corridors={corridors}
        apiKey={MAPS_API_KEY}
        region={MAPS_REGION}
        elopementRing={elopementOn ? elopementRing : []}
        elopementDrawMode={elopementOn ? elopementDrawMode : false}
        onElopementClick={elopementOn ? onElopementClick : undefined}
      />
    </>
  );
}

ClientsMapPanel.propTypes = {
  /** When true AND the commandcenter feature flag is on, show draw/save UI. */
  enableElopementBoundary: PropTypes.bool,
};
